import React, { useEffect, useState } from "react";
import API from "../../api";
import toastr from "toastr";

interface User {
  id?: number;
  full_name?: string;
  [key: string]: any;
}

interface PermissionModalProps {
  show: boolean;
  onClose: () => void;
  userData?: User;
  onSaved?: () => void;
}

// All permission keys that can be toggled
const ALL_PERMISSION_KEYS = [
  "dashboard",
  "users",
  "adduser",
  "edituser",
  "deleteuser",
  "manage_user_permission",
  "patients",
  "addpatient",
  "editpatient",
  "deletepatient",
  "investigation",
  "doctor",
  "add_doctor",
  "edit_doctor",
  "delete_doctor",
  "billing",
  // Report
  "report",
  "view_report",
  "delete_report",
  // Payments & Finance
  "payments",
  "finance",
  "income",
  "expense",
  "finance_report",
  // Test Settings
  "test_settings",
];

const buildDefaultPermissions = (): Record<string, boolean> =>
  Object.fromEntries(ALL_PERMISSION_KEYS.map((k) => [k, false]));

const PermissionModal: React.FC<PermissionModalProps> = ({
  show,
  onClose,
  userData,
  onSaved,
}) => {
  const [permissions, setPermissions] = useState<Record<string, boolean>>(
    buildDefaultPermissions()
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && userData) {
      setLoading(true);
      const updated: Record<string, boolean> = buildDefaultPermissions();
      ALL_PERMISSION_KEYS.forEach((key) => {
        updated[key] = userData[key] === 1 || userData[key] === true;
      });
      setPermissions(updated);
      setLoading(false);
    } else if (!show) {
      setPermissions(buildDefaultPermissions());
    }
  }, [show, userData]);

  const togglePermission = (key: string) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSection = (keys: string[]) => {
    const allOn = keys.every((k) => permissions[k]);
    setPermissions((prev) => {
      const updated = { ...prev };
      keys.forEach((k) => (updated[k] = !allOn));
      return updated;
    });
  };

  const handleSave = async () => {
    if (!userData?.id) {
      toastr.error("Invalid user ID");
      return;
    }

    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(permissions).map(([k, v]) => [k, v ? 1 : 0])
      );

      await API.put(`/users/updateUserPermission/${userData.id}`, payload);
      toastr.success("Permissions updated successfully!");
      onSaved?.();
      onClose();
    } catch (err: any) {
      console.error(err);
      toastr.error(
        err.response?.data?.message || "Failed to update permissions"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const renderSection = (
    title: string,
    icon: string,
    color: string,
    items: { key: string; label: string }[]
  ) => {
    const sectionKeys = items.map((i) => i.key);
    const allOn = sectionKeys.every((k) => permissions[k]);
    const someOn = sectionKeys.some((k) => permissions[k]);

    return (
      <div className={`card border-${color} mb-3`} key={title}>
        <div className="card-header bg-transparent py-2 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className={`fas ${icon} text-${color} me-2`}></i>
            <h6 className={`mb-0 text-${color} fw-bold`}>{title}</h6>
          </div>
          <button
            type="button"
            className={`btn btn-sm ${
              allOn
                ? `btn-${color}`
                : someOn
                ? `btn-outline-${color}`
                : "btn-outline-secondary"
            }`}
            style={{ fontSize: "0.7rem", padding: "2px 8px" }}
            onClick={() => toggleSection(sectionKeys)}
          >
            {allOn ? "Deselect All" : "Select All"}
          </button>
        </div>
        <div className="card-body py-2 px-3">
          {items.map((perm) => (
            <div
              key={perm.key}
              className="d-flex align-items-center justify-content-between py-2"
              style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
            >
              <label
                htmlFor={perm.key}
                className="fw-medium mb-0"
                style={{ cursor: "pointer", fontSize: "0.9rem" }}
              >
                {perm.label}
              </label>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={perm.key}
                  checked={permissions[perm.key] ?? false}
                  onChange={() => togglePermission(perm.key)}
                  style={{ width: "2.5em", height: "1.25em", cursor: "pointer" }}
                />
                <label
                  className="form-check-label ms-1"
                  htmlFor={perm.key}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className={`badge ${
                      permissions[perm.key] ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {permissions[perm.key] ? "ON" : "OFF"}
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 1060,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-dialog modal-md modal-dialog-scrollable modal-dialog-centered"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-content shadow-lg border-0">
          {/* Header */}
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title mb-0 d-flex align-items-center">
              <i className="fas fa-user-shield me-2"></i>
              <span>Manage User Permissions</span>
            </h5>
            {userData?.full_name && (
              <small className="text-white-50 ms-3 d-none d-md-block">
                {userData.full_name}
              </small>
            )}
            <button
              type="button"
              className="btn-close btn-close-white ms-auto"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div
            className="modal-body bg-light"
            style={{ maxHeight: "65vh", overflowY: "auto" }}
          >
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading permissions...</p>
              </div>
            ) : (
              <>
                {renderSection("Dashboard", "fa-tachometer-alt", "primary", [
                  { key: "dashboard", label: "Dashboard Access" },
                ])}

                {renderSection("Users", "fa-users", "success", [
                  { key: "users", label: "View Users" },
                  { key: "adduser", label: "Add User" },
                  { key: "edituser", label: "Edit User" },
                  { key: "deleteuser", label: "Delete User" },
                  { key: "manage_user_permission", label: "Manage User Permission" },
                ])}

                {renderSection("Patients", "fa-user-injured", "info", [
                  { key: "patients", label: "View Patients" },
                  { key: "addpatient", label: "Add Patient" },
                  { key: "editpatient", label: "Edit Patient" },
                  { key: "deletepatient", label: "Delete Patient" },
                ])}

                {renderSection("Doctor", "fa-user-md", "dark", [
                  { key: "doctor", label: "View Doctors" },
                  { key: "add_doctor", label: "Add Doctor" },
                  { key: "edit_doctor", label: "Edit Doctor" },
                  { key: "delete_doctor", label: "Delete Doctor" },
                ])}

                {renderSection("Report", "fa-file-medical", "warning", [
                  { key: "report", label: "Report Access" },
                  { key: "view_report", label: "View Report" },
                  { key: "delete_report", label: "Delete Report" },
                ])}

                {renderSection("Finance", "fa-wallet", "danger", [
                  { key: "payments", label: "Payments" },
                  { key: "finance", label: "Finance" },
                  { key: "income", label: "Income" },
                  { key: "expense", label: "Expense" },
                  { key: "finance_report", label: "Finance Report" },
                ])}

                {renderSection("Other", "fa-th-large", "secondary", [
                  { key: "investigation", label: "Investigation" },
                  { key: "billing", label: "Billing" },
                  { key: "test_settings", label: "Test Settings" },
                ])}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-top">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={saving}
            >
              <i className="fas fa-times me-1"></i> Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary px-4"
              disabled={saving || loading}
              onClick={handleSave}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i> Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionModal;